import { Box, Flex, Tag } from '@chakra-ui/react';
import React from 'react';

import type { FieldItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import {
  BAR_BG,
  BAR_BORDER,
  CASE_CONTEXT_BAR_H,
  CASE_CONTEXT_TITLE_LETTER_SPACING,
  CHIP_BORDER,
  CHIP_BORDER_HOVER,
  COMPACT_FIELD_LABEL_PAD,
  COMPACT_FIELD_VALUE_PAD,
  FIELD_LABEL_BG,
  FIELD_LABEL_DIVIDER,
  FIELD_LABEL_FONT_SIZE,
  FIELD_LABEL_FONT_WEIGHT,
  FIELD_LABEL_LETTER_SPACING,
  FIELD_LABEL_PAD,
  FIELD_VALUE_BG,
  FIELD_VALUE_PAD,
  INK,
  INK_MUTED,
  SANS_STACK,
} from '../../styles/tokens';
import { Slot } from '../Slot';
import { VerticalDivider } from '../VerticalDivider';

/** Shown in the value segment when a field has no bound value. Matches `PayerInfo`'s placeholder. */
const FIELD_VALUE_PLACEHOLDER = 'N/A';

/** Ascending by `order`, dropping any field marked `visible:false` -- the ordering/visibility
 *  contract every `FieldItem`-driven container in this folder follows. Mirrors
 *  `PatientSummaryBar`'s helper of the same shape; kept local (not shared) so each molecule folder
 *  stays self-contained, matching `ResizableSplit/clampPct.ts`'s precedent. `.filter` on a
 *  `readonly FieldItem[]` already returns a fresh mutable array, so `.sort` never mutates the
 *  config's own (readonly, `as const`) field list. */
function visibleFieldsInOrder(fields: readonly FieldItem[]): FieldItem[] {
  return fields
    .filter((field) => field.visible)
    .sort((a, b) => a.order - b.order);
}

/**
 * Case context bar -- Patient Type, Payer, Flags, and a right-pinned Routing Context ("why routed").
 * By default `header.caseContext.fields` is `[]` so this renders an empty,
 * labelled `role="region"` container; when a config supplies fields, each renders as a reused
 * Chakra `Tag`, proving the atom-reuse requirement.
 *
 * `header.caseContext.visible` gates the CONTAINER itself, same geometry-preserving hide contract
 * as `TopBarContainer`/`PatientSummaryBar`: `visible:false` sets `visibility:hidden` +
 * `aria-hidden`, never `display:none`, so fixed header geometry never collapses; `data-content-key`
 * stays present in BOTH states so tests/future wiring can always find the container.
 *
 * Each field's `Tag` is NON-INTERACTIVE chrome the host always renders (`key`/`data-slot-key`/
 * `data-testid` stay on the `Tag`); the field's BODY is a `Slot` reading
 * `field.contentId`'s bound value out of injected `state` (`slots/readState.ts`) -- when
 * `field.componentId` resolves in the injected registry the component renders in place of plain
 * text, otherwise the fallback renders the bound value as text, or `FIELD_VALUE_PLACEHOLDER` when
 * there is no bound value.
 *
 * The `Tag` is a two-segment `LABEL | value` pill sharing one outer border. That chrome lives in the
 * host, not in any one slot component, because the design applies it to every case-context field, so
 * each new field inherits it.
 *
 */
export const CaseContextBar: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { contentKey, visible, fields, routingContext } =
    config.header.caseContext;
  const routingValue = routingContext
    ? readState(state, routingContext.contentId)
    : undefined;

  return (
    <Flex
      role="region"
      aria-label="Case context"
      data-content-key={contentKey}
      aria-hidden={visible ? undefined : true}
      align="center"
      gap={2}
      fontFamily={SANS_STACK}
      bg={BAR_BG}
      borderBottom={`1px solid ${BAR_BORDER}`}
      // `overflow` is the backstop for an over-wide row — with a fixed height and no wrapping it
      // would otherwise paint outside the bar. Fields shrink first, via the `Tag` below.
      paddingX={5}
      overflow="hidden"
      style={{
        height: CASE_CONTEXT_BAR_H,
        minHeight: CASE_CONTEXT_BAR_H,
        visibility: visible ? 'visible' : 'hidden',
        gap: "8px",
      }}
    >
      <Box
        as="span"
        data-testid="case-context-title"
        flexShrink={0}
        color={INK_MUTED}
        textTransform="uppercase"
        fontSize={FIELD_LABEL_FONT_SIZE}
        fontWeight={FIELD_LABEL_FONT_WEIGHT}
        letterSpacing={CASE_CONTEXT_TITLE_LETTER_SPACING}
        lineHeight={1}
      >
        Case Context
      </Box>
      {visibleFieldsInOrder(fields).map((field) => {
        const value = readState(state, field.contentId);
        return (
          <Tag
            key={field.contentKey}
            data-slot-key={field.contentKey}
            data-testid="field-tag"
            // The `Tag` is the pill SHELL, not a padded chip: the segments supply their own
            // backgrounds and insets, and `overflow` clips their square corners to the rounded shell.
            display="inline-flex"
            alignItems="stretch"
            padding={0}
            bg="transparent"
            border={`1px solid ${CHIP_BORDER}`}
            borderRadius="9999px"
            overflow="hidden"
            // A permanent label segment raises every field's minimum width, so once a second field
            // lands the row would overflow the bar without this.
            flexShrink={1}
            minWidth={0}
            _hover={{ borderColor: CHIP_BORDER_HOVER }}
          >
            {/* `as="span"`: the host `Tag` is a <span>, which may not contain Box's default <div>.
                An empty label renders nothing rather than a padded grey nub — `Item.label` has no
                non-empty rule in the schema. */}
            {field.label ? (
              <Box
                as="span"
                data-testid="field-label"
                display="flex"
                alignItems="center"
                flexShrink={0}
                bg={FIELD_LABEL_BG}
                color={INK_MUTED}
                textStyle="Overline 1"
                fontFamily={SANS_STACK}
                textTransform="uppercase"
                fontSize={FIELD_LABEL_FONT_SIZE}
                fontWeight={FIELD_LABEL_FONT_WEIGHT}
                letterSpacing={FIELD_LABEL_LETTER_SPACING}
                padding={
                  field.valueChrome === 'compact'
                    ? COMPACT_FIELD_LABEL_PAD
                    : FIELD_LABEL_PAD
                }
                lineHeight={1}
                borderRight={`1px solid ${FIELD_LABEL_DIVIDER}`}
              >
                {field.label}
              </Box>
            ) : null}
            <Box
              as="span"
              data-testid="field-value"
              display="flex"
              alignItems="center"
              minWidth={0}
              bg={FIELD_VALUE_BG}
              color={INK}
              fontFamily={SANS_STACK}
              fontSize="11.5px"
              fontWeight={500}
              lineHeight={1}
              whiteSpace="nowrap"
              padding={
                field.valueChrome === 'compact'
                  ? COMPACT_FIELD_VALUE_PAD
                  : FIELD_VALUE_PAD
              }
            >
              <Slot
                item={field}
                value={value}
                mode={mode}
                // Must NOT fall back to `field.label` — the label owns its own segment, so that
                // would render it twice (`PAYER` `Payer`). `renderPlainText` still throws on a
                // non-primitive bound to a `componentId`-less slot; that is a config bug, stay loud.
                fallback={() =>
                  renderPlainText(value) ?? FIELD_VALUE_PLACEHOLDER
                }
              />
            </Box>
          </Tag>
        );
      })}
      {routingContext ? (
        <Flex
          as="span"
          data-slot-key={routingContext.contentKey}
          data-testid="case-context-routing"
          align="center"
          gap="8px"
          flexShrink={0}
          marginLeft="auto"
        >
          <VerticalDivider />
          <Slot
            item={routingContext}
            value={routingValue}
            mode={mode}
            fallback={() =>
              renderPlainText(routingValue) ?? FIELD_VALUE_PLACEHOLDER
            }
          />
        </Flex>
      ) : null}
    </Flex>
  );
};
